import { yupResolver } from '@hookform/resolvers/yup';
import { InsuredPeopleDto, OnboardingPeriodDto } from '@zorro/clients';
import { UNEXPECTED_ERROR_MESSAGE } from '@zorro/common-utils';
import { mapInsuredIdsToNameMap } from '@zorro/types';
import {
  getWhoIsEnrolled,
  showErrorNotification,
  useForm,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useAnalytics } from '../Analytics';
import {
  PlanElectionFormFields,
  PlanElectionItemFormFields,
  getWhoIsEnrolledInMultiplePlans,
  planElectionFormSchema,
} from './PlanElectionFormUtils';

export type UsePlanElectionFormSubmitProps = {
  data: PlanElectionFormFields;
  onboardingPeriod: OnboardingPeriodDto;
  insuredIdToNameMap: Map<string, string>;
};

type UsePlanElectionForm = (props: {
  onboardingPeriodId: string;
  employeeId: string;
  getDefaultFormFields: (
    allWhoIsEnrolled: string[]
  ) => PlanElectionItemFormFields[];
  onSubmit: (props: UsePlanElectionFormSubmitProps) => Promise<boolean>;
  onPlansChange?: (plans: PlanElectionItemFormFields[]) => void;
}) => {
  onSubmit: () => Promise<boolean>;
  planElectionForm: UseFormReturn<PlanElectionFormFields>;
  insuredIdToNameMap: Map<string, string>;
  isCombinedPlanStructure?: boolean;
  onboardingPeriod?: OnboardingPeriodDto;
  submittedWhoIsEnrolledOptions: string[];
  selectedEnrolled: Set<string>;
  allWhoIsEnrolledOptions: string[];
  whoIsEnrolledInMultiplePlans: string[];
  insured?: InsuredPeopleDto;
  isSubmitting: boolean;
};

export const usePlanElectionForm: UsePlanElectionForm = ({
  employeeId,
  onboardingPeriodId,
  getDefaultFormFields,
  onPlansChange,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { sendEvent } = useAnalytics();

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });
  const { data: insured } = useMonolithQuery({
    method: 'insuredControllerFindInsuredForPeriod',
    params: [onboardingPeriodId],
  });
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const submittedWhoIsEnrolledOptions = benefits?.majorMedicalBenefit
    ?.insuredPeople
    ? getWhoIsEnrolled(benefits?.majorMedicalBenefit?.insuredPeople)
    : [];
  const allWhoIsEnrolledOptions = insured ? getWhoIsEnrolled(insured) : [];

  const planElectionForm = useForm<PlanElectionFormFields>({
    mode: 'all',
    resolver: yupResolver(planElectionFormSchema),
    defaultValues: {
      plans: getDefaultFormFields(
        submittedWhoIsEnrolledOptions.length > 0
          ? submittedWhoIsEnrolledOptions
          : allWhoIsEnrolledOptions
      ),
    },
  });

  const insuredIdToNameMap = mapInsuredIdsToNameMap(insured);

  const plans = planElectionForm.watch('plans');
  const selectedEnrolled = new Set(plans.flatMap((plan) => plan.whoIsEnrolled));
  const whoIsEnrolledInMultiplePlans = getWhoIsEnrolledInMultiplePlans(
    allWhoIsEnrolledOptions,
    plans
  );

  useEffect(() => {
    if (!isSubmitting) {
      onPlansChange?.(plans);
    }
  }, [isSubmitting, onPlansChange, JSON.stringify(plans)]);

  return {
    insured,
    isSubmitting,
    allWhoIsEnrolledOptions,
    submittedWhoIsEnrolledOptions,
    selectedEnrolled,
    planElectionForm,
    onboardingPeriod,
    insuredIdToNameMap,
    whoIsEnrolledInMultiplePlans,
    onSubmit: async () => {
      if (!insured || !onboardingPeriod) {
        showErrorNotification({ message: UNEXPECTED_ERROR_MESSAGE });
        return false;
      }

      const data = planElectionForm.getValues();
      if (!selectedEnrolled.has(insured.employee.id)) {
        showErrorNotification({
          message: 'The employee must be listed on a plan',
        });
        await sendEvent('plan_election_validation_error', {
          onboarding_period_id: onboardingPeriodId,
          reason: 'employee_not_covered',
        });
        return false;
      }

      let result = false;
      setIsSubmitting(true);
      try {
        result = await onSubmit({
          onboardingPeriod,
          insuredIdToNameMap,
          data,
        });
      } catch {
        setIsSubmitting(false);
      }

      return result;
    },
  };
};
